import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, Loader } from "@/components/common";
import { TextField, SelectField, AreaField, DateField } from "@/components/fields";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClipboardText, Plus, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = ["Move-In", "Routine", "Maintenance", "Move-Out"];
const STATUSES = ["scheduled", "in_progress", "completed"];
const empty = { property_id: "", inspection_type: "Routine", scheduled_date: "", notes: "", damages: "", repair_recommendations: "", status: "scheduled", photos: [] };

export default function Inspections() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/inspections").then((r) => setItems(r.data));
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.property_id) return toast.error("Select a property");
    try {
      if (editId) await api.put(`/inspections/${editId}`, form);
      else await api.post("/inspections", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!items) return <Loader />;

  return (
    <div>
      <PageHeader title="Inspections" subtitle="Move-in, routine, maintenance & move-out" icon={ClipboardText}
        actions={<Button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-inspection-btn" className="gap-2"><Plus size={16} /> New Inspection</Button>} />

      {items.length === 0 ? (
        <EmptyState icon={ClipboardText} title="No inspections yet" message="Schedule an inspection for any property."
          action={<Button onClick={() => setOpen(true)}><Plus size={16} className="mr-2" />New Inspection</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Property</TableHead><TableHead>Type</TableHead><TableHead>Inspector</TableHead>
                <TableHead>Scheduled</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((i) => (
                  <TableRow key={i.id} data-testid={`inspection-row-${i.id}`}>
                    <TableCell className="font-medium">{i.property_name}</TableCell>
                    <TableCell><Badge variant="outline">{i.inspection_type}</Badge></TableCell>
                    <TableCell className="text-sm">{i.staff_name}</TableCell>
                    <TableCell className="text-sm">{fmtDate(i.scheduled_date)}</TableCell>
                    <TableCell><StatusBadge status={i.status} /></TableCell>
                    <TableCell className="text-right"><Button size="icon" variant="ghost" onClick={() => { setForm({ ...empty, ...i }); setEditId(i.id); setOpen(true); }}><PencilSimple size={16} /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Inspection" : "New Inspection"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <SelectField label="Property" value={form.property_id} onChange={(v) => set("property_id", v)}
                options={props.map((p) => ({ value: p.id, label: `${p.name} ${p.unit_no || ""}` }))} required testId="inspection-property" />
              <SelectField label="Type" value={form.inspection_type} onChange={(v) => set("inspection_type", v)} options={TYPES} />
              <DateField label="Scheduled Date" value={form.scheduled_date} onChange={(v) => set("scheduled_date", v)} />
              <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} options={STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, " ") }))} />
            </div>
            <AreaField label="Notes" value={form.notes} onChange={(v) => set("notes", v)} />
            <AreaField label="Damages" value={form.damages} onChange={(v) => set("damages", v)} />
            <AreaField label="Repair Recommendations" value={form.repair_recommendations} onChange={(v) => set("repair_recommendations", v)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-inspection-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
